import { BillingPlanKey, LegacyPlanKey, PackagePlanKey } from "../types/organizations";

/**
 * The plans as the dashboard presents them. The same table as the backend's
 * backend-services/common/src/types/plans.ts (PLAN_CATALOGUE, PLAN_FEATURE_SET,
 * FEATURE_PACKAGE), which decides access; keep the two in step. Stripe holds
 * the real prices (planPrices from getBillingStatus); these figures are the
 * fallback shown when Stripe's are not loaded. The site's prices are in
 * website/lib/pricing.ts.
 *
 * The packages (Live, Verified Data, LinkedIn Integration, Everything, and
 * Dial faster on its own or as an add-on) are what checkout sells. The legacy
 * plans (Verify, Enrich, Prospector) stay for the customers on them: they keep
 * them, and may move between them, but nobody new can buy one.
 */
export const LEGACY_PLAN_KEYS: LegacyPlanKey[] = ["verify", "enrich", "prospector"];
/** The packages checkout shows as columns; Dial faster is offered beside them as an add-on or on its own. */
export const PACKAGE_PLAN_KEYS: PackagePlanKey[] = ["data", "linkedin", "everything"];
export const ALL_PLAN_KEYS: BillingPlanKey[] = [...LEGACY_PLAN_KEYS, ...PACKAGE_PLAN_KEYS, "dialFaster"];
/** Kept for older imports: the legacy tiers, in order. */
export const PLAN_KEYS: LegacyPlanKey[] = LEGACY_PLAN_KEYS;

export const isLegacyPlan = (plan: BillingPlanKey | null | undefined): plan is LegacyPlanKey => LEGACY_PLAN_KEYS.includes(plan as LegacyPlanKey);
export const isPackagePlan = (plan: BillingPlanKey | null | undefined): plan is PackagePlanKey =>
  [...PACKAGE_PLAN_KEYS, "dialFaster"].includes(plan as PackagePlanKey);

export interface PlanCatalogueEntry {
  name: string;
  monthlyUsd: number;
  yearlyUsd: number;
  /** One line on what it is for. */
  description: string;
  includes: string[];
  /** Kept for the customers on it; not sold to new purchases. */
  legacy: boolean;
}

export const PLAN_CATALOGUE: Record<BillingPlanKey, PlanCatalogueEntry> = {
  verify: {
    name: "Verify",
    monthlyUsd: 39,
    yearlyUsd: 399,
    description: "Email verification, its status fields, and invalid email handling.",
    includes: ["Email verification", "Email status fields in your CRM", "Invalid email handling"],
    legacy: true,
  },
  enrich: {
    name: "Enrich",
    monthlyUsd: 59,
    yearlyUsd: 599,
    description: "Everything in Verify, plus email and profile enrichment and Export.",
    includes: ["Everything in Verify", "Email enrichment", "Profile enrichment: LinkedIn profile sync and job changes", "Export from LinkedIn to your CRM"],
    legacy: true,
  },
  prospector: {
    name: "Prospector",
    monthlyUsd: 88,
    yearlyUsd: 799,
    description: "Everything in Enrich, plus LinkedIn automation, LinkedIn DMs and activity in your CRM, and Dial faster.",
    includes: [
      "Everything in Enrich",
      "LinkedIn automation: connection requests, messages and sequence LinkedIn steps",
      "LinkedIn DMs in your CRM",
      "LinkedIn activity feed",
      "Dial faster for Outreach and HubSpot",
    ],
    legacy: true,
  },
  data: {
    name: "Live, Verified Data",
    monthlyUsd: 79,
    yearlyUsd: 799,
    description: "Verified and enriched profiles and emails, and LinkedIn and Sales Navigator exports into your CRM.",
    includes: [
      "Email verification and status fields in your CRM",
      "Email enrichment: finds a valid work email and makes it primary",
      "Profile enrichment from LinkedIn and job change detection",
      "Export LinkedIn and Sales Navigator profiles and companies to your CRM",
    ],
    legacy: false,
  },
  linkedin: {
    name: "LinkedIn Integration",
    monthlyUsd: 59,
    yearlyUsd: 599,
    description: "LinkedIn embedded in your CRM, with activity logging and LinkedIn tasks completed for you.",
    includes: [
      "LinkedIn DMs embedded in your CRM",
      "LinkedIn activity feed, logged on the record",
      "LinkedIn connection requests and messages, sent for you",
      "LinkedIn steps in sequences done and their tasks completed for you",
    ],
    legacy: false,
  },
  everything: {
    name: "Everything",
    monthlyUsd: 99,
    yearlyUsd: 999,
    description: "Live, Verified Data, LinkedIn Integration and Dial faster in one bundle.",
    includes: ["Everything in Live, Verified Data", "Everything in LinkedIn Integration", "Dial faster, the power dialer, included"],
    legacy: false,
  },
  dialFaster: {
    name: "Dial faster",
    monthlyUsd: 39,
    yearlyUsd: 399,
    description: "The power dialer on your call task queue. Included in Everything.",
    includes: ["Dial faster, the power dialer on your call task queue", "Every call outcome logged"],
    legacy: false,
  },
};

/** Whether a plan includes Dial faster itself (no add-on needed). */
export const planIncludesDialer = (plan: BillingPlanKey): boolean => plan === "prospector" || plan === "everything" || plan === "dialFaster";

/**
 * What checkout offers: the backend's sellablePlans (the packages, or the
 * legacy plans until the package prices are configured), else the packages.
 */
export const checkoutPlans = (sellable: BillingPlanKey[] | null | undefined): BillingPlanKey[] =>
  sellable && sellable.length ? sellable : [...PACKAGE_PLAN_KEYS, "dialFaster"];

/**
 * What Change plan lists: while on a legacy plan, the legacy plans (to move
 * between, as before) and what is for sale; on a package, what is for sale.
 * The backend's plansOfferedFor.
 */
export const changePlanOptions = (current: BillingPlanKey, sellable: BillingPlanKey[] | null | undefined): BillingPlanKey[] => {
  const forSale = checkoutPlans(sellable);
  const legacy = isLegacyPlan(current) ? LEGACY_PLAN_KEYS : [];
  return [...legacy, ...forSale.filter((p) => !(legacy as BillingPlanKey[]).includes(p))];
};

/** On every plan, billed by use. */
export const USAGE_BASED_NOTE = "On every plan, billed at cost by use: phone verification and enrichment, and caller ID health.";

export type PlanFeature =
  | "emailVerification"
  | "emailEnrichment"
  | "profileEnrichment"
  | "export"
  | "linkedinAutomation"
  | "linkedinInbox"
  | "linkedinActivity"
  | "dialer";

/** The least legacy plan each gated feature needs. */
export const FEATURE_PLAN: Record<PlanFeature, LegacyPlanKey> = {
  emailVerification: "verify",
  emailEnrichment: "enrich",
  profileEnrichment: "enrich",
  export: "enrich",
  linkedinAutomation: "prospector",
  linkedinInbox: "prospector",
  linkedinActivity: "prospector",
  dialer: "prospector",
};

/** The package that sells each gated feature (backend FEATURE_PACKAGE): what an upgrade prompt asks for. */
export const FEATURE_PACKAGE: Record<PlanFeature, PackagePlanKey> = {
  emailVerification: "data",
  emailEnrichment: "data",
  profileEnrichment: "data",
  export: "data",
  linkedinAutomation: "linkedin",
  linkedinInbox: "linkedin",
  linkedinActivity: "linkedin",
  dialer: "dialFaster",
};

/** Every gated feature each plan includes (backend PLAN_FEATURE_SET, without the usage-based ones). */
const PLAN_FEATURE_SET: Record<BillingPlanKey, PlanFeature[]> = {
  verify: ["emailVerification"],
  enrich: ["emailVerification", "emailEnrichment", "profileEnrichment", "export"],
  prospector: ["emailVerification", "emailEnrichment", "profileEnrichment", "export", "linkedinAutomation", "linkedinInbox", "linkedinActivity", "dialer"],
  data: ["emailVerification", "emailEnrichment", "profileEnrichment", "export"],
  linkedin: ["linkedinAutomation", "linkedinInbox", "linkedinActivity"],
  everything: ["emailVerification", "emailEnrichment", "profileEnrichment", "export", "linkedinAutomation", "linkedinInbox", "linkedinActivity", "dialer"],
  dialFaster: ["dialer"],
};

/** "This needs the LinkedIn Integration plan", the words the backend refuses with. */
export const planRequiredMessage = (feature: PlanFeature): string => {
  const plan = FEATURE_PACKAGE[feature];
  return plan === "dialFaster" ? "This needs the Dial faster add-on" : `This needs the ${PLAN_CATALOGUE[plan].name} plan`;
};

/** True when a callable refused for the plan (functions/src/services/plan-gate.ts). */
export const isPlanRequiredError = (error: unknown): boolean => {
  const e = error as { code?: string; details?: { error?: string }; message?: string } | null;
  return (
    !!e &&
    (e.details?.error === "plan_required" ||
      /needs the (Verify|Enrich|Prospector|Live, Verified Data|LinkedIn Integration|Everything) plan|needs the Dial faster add-on/.test(String(e.message || "")))
  );
};

/** The dashboard link the extension, the HubSpot app and the MCP send people to. */
export const BILLING_DEEP_LINK = "/app/dashboard?open=billing";

interface IPlanOrg {
  entitlementsVersion?: number | null;
  billingPlan?: BillingPlanKey | null;
  isEnrichmentEnabled?: boolean;
  isLinkedinEnrichmentEnabled?: boolean;
  featureFlags?: Partial<Record<string, boolean>>;
  addons?: Partial<Record<string, { status?: string | null } | null>>;
  isTrial?: boolean;
  isTrialExpired?: boolean;
  isPayingCustomer?: boolean;
  trialDetails?: { expiresAt?: string | null } | null;
  billing?: { status?: string | null } | null;
}

const ACTIVE_ADDON = ["active", "trialing", "past_due"];

/**
 * On a free trial with nothing paid yet, which includes everything: the same
 * rule as the backend's isOnFreeTrial (common/src/types/plans.ts).
 */
export const isOnFreeTrial = (org: IPlanOrg | null | undefined): boolean => {
  if (!org || org.isTrial !== true || org.isPayingCustomer === true || org.isTrialExpired === true) return false;
  if (["active", "past_due"].includes(String(org.billing?.status || ""))) return false;
  const expiresAt = org.trialDetails?.expiresAt ? Date.parse(org.trialDetails.expiresAt) : NaN;
  return !(Number.isFinite(expiresAt) && expiresAt <= Date.now());
};

/**
 * True when the organization's plan leaves the feature out: the same answer
 * as the backend's !planIncludes, for showing an upgrade prompt. Always false
 * for organizations from before plan gating (no entitlementsVersion) that
 * have not bought a package.
 */
export const orgPlanLocks = (org: IPlanOrg | null | undefined, feature: PlanFeature): boolean => {
  // Gated: created after plan gating, or on a package (backend isPlanGated).
  if (!org || (Number(org.entitlementsVersion || 0) < 2 && !isPackagePlan(org.billingPlan))) return false;
  // Free trials include everything (backend isOnFreeTrial).
  if (isOnFreeTrial(org)) return false;
  const plan: BillingPlanKey = org.billingPlan && ALL_PLAN_KEYS.includes(org.billingPlan) ? org.billingPlan : org.isEnrichmentEnabled ? "enrich" : "verify";
  if (PLAN_FEATURE_SET[plan].includes(feature)) return false;
  if (feature === "emailEnrichment" && org.isEnrichmentEnabled === true) return false;
  if (feature === "profileEnrichment" && org.isLinkedinEnrichmentEnabled === true) return false;
  if (feature === "dialer" && (org.featureFlags?.dialer === true || ACTIVE_ADDON.includes(String(org.addons?.dialer?.status || "")))) return false;
  return true;
};
